import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'

import T from 'T'

type OverCapacityVariant = 'panel' | 'caption'

interface OverCapacityNoticeProps {
  truncated?: boolean
  totalMatched?: number
  cap?: number
  variant: OverCapacityVariant
}

/**
 * Over-capacity notice for the scheduling assignment map. The backend caps the query to the
 * nearest-N services; when it truncates, this tells the user the map is showing a subset and how
 * to narrow the search. Renders nothing when the result set wasn't capped.
 *
 * `panel` sits in the filters column and owns the "how to fix it" line; `caption` is the terse echo
 * inside the map's radius control. `variant` is required — every caller is `.jsx`, so a default
 * would silently absorb a typo'd value instead of rendering something visibly wrong.
 */
const OverCapacityNotice: React.FC<OverCapacityNoticeProps> = ({ truncated = false, totalMatched = 0, cap = 0, variant }) => {
  if (!truncated) return null

  const capText = cap.toLocaleString()
  const matchedText = totalMatched.toLocaleString()

  if (variant === 'caption') {
    return (
      <Stack alignItems="center" direction="row" spacing={0.5}>
        <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'warning.main' }} />
        <Typography color="text.primary" variant="body2">
          {`${T.SCHEDULING_RESULTS_CAPPED_SHORT_PREFIX} ${capText} ${T.SCHEDULING_RESULTS_CAPPED_MIDDLE} ${matchedText}`}
        </Typography>
      </Stack>
    )
  }

  return (
    <Alert
      icon={<WarningAmberRoundedIcon sx={{ fontSize: 20 }} />}
      severity="warning"
      sx={{ border: '1px solid', borderColor: 'warning.light', alignItems: 'flex-start' }}
    >
      <AlertTitle sx={{ mb: 0.25 }}>
        {`${T.SCHEDULING_RESULTS_CAPPED_PREFIX} ${capText} ${T.SCHEDULING_RESULTS_CAPPED_MIDDLE} ${matchedText}`}
      </AlertTitle>
      <Typography color="text.secondary" variant="body2">
        {T.SCHEDULING_RESULTS_CAPPED_ACTION}
      </Typography>
    </Alert>
  )
}

export default OverCapacityNotice
